// lib
import { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { isEqual } from 'lodash'

import ADND from '@aytm/lib/adnd/ADND'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import SchemaRules from '@aytm/app/stats/lib/query/schema-rules'

import { DataContext } from '@aytm/app/stats/page/question-views/vq/Component'

import { usePrevious } from '@aytm/app/insights/dashboard/components/hooks'
import { generateUID, encodeText, decodeText } from '@aytm/app/stats/lib/util/helpers'
import { getLogicData, getRefs, getClustersRefs } from '@aytm/app/stats/lib/query/refs'
import { convertValuesToTree, convertTreeToValues } from '@aytm/app/stats/lib/util/converter'

// components
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { MultiStateMultiSelect } from 'primereact/multistatemultiselect'

import DropdownSlider from 'primereact-raytm-theme/DropdownSlider'
import MultiSelectHeader from 'primereact-raytm-theme/MultiSelectHeader'
import MultiSelectFooter from 'primereact-raytm-theme/MultiSelectFooter'

import TextPopup from '@aytm/lib/rfd-ui/components/basic/text-popup'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import AdvancedFilterContainer from '@aytm/app/stats/components/advanced-filter/AdvancedFilterContainer'

import ModeToggle from '@aytm/app/stats/page/question-views/vq/components/ModeToggle'
import AnswerVariantOptions from '@aytm/app/stats/page/question-views/vq/components/AnswerVariantOptions'

import ImagesDropdown from '@aytm/app/stats/components/images-dropdown/ImagesDropdown'

import { itemTemplate, valueTemplate } from '@aytm/app/stats/page/components/lib/templates'

// hooks
import useSingleAndDoubleClick from '@aytm/app/stats/page/components/hooks/useSingleAndDoubleClick'

// styles
import style from '@aytm/app/stats/page/question-views/vq/styles/Component.module.scss'

const AnswerVariant = ({ item, position, dndItemsList }) => {
  const { id, data, disabled, dispatch, dropdownsRef, parserResultPatternBasicsRef, basicFilters, parserResultEof } =
    useContext(DataContext)

  const [processing, setProcessing] = useState(false)

  const [baseMode, setBaseMode] = useState(false)

  const [basePattern, setBasePattern] = useState(item.base || '')

  const [selectedValue, setSelectedValue] = useState(null)
  const [selectedValues, setSelectedValues] = useState([])
  const [selectedOption, setSelectedOption] = useState(null)

  const [checkPattern, setCheckPattern] = useState('advanced' != item.mode || false != item.check)

  const [input, setInput] = useState('')

  const timer = useRef(null)

  const changedRef = useRef(0 < item.pattern.trim().length)

  const dndListItemRef = useRef()
  const dndListItemHandleRef = useRef()

  const callbackRef = useRef(null)

  const selectedOptionRef = useRef(null)
  selectedOptionRef.current = selectedOption

  const parserResultPattern = useMemo(
    () =>
      data.parser.analyzeString({
        id: id,
        filter: item.pattern.trim(),
        clusters: data.clusters,
        saveTokens: true
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.pattern, id, data.surveySchema, data.clusters]
  )
  const parserResultPatternPrev = usePrevious(parserResultPattern)

  const parserResultPatternError = useMemo(
    () =>
      0 < item.pattern.trim().length &&
      0 < parserResultPattern.expression.status.errors.length &&
      parserResultPattern.expression.status.errors.some(v => !['ref', 'cluster'].includes(v.type)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parserResultPattern]
  )

  const [parserResultPatternBasic, setParserResultPatternBasic] = useState({})

  useEffect(() => {
    try {
      const tree = parserResultPatternError
        ? null
        : 0 < item.pattern.trim().length
        ? 0 < parserResultPattern.expression.status.errors.length
          ? null
          : checkPattern
          ? parserResultPattern &&
            parserResultPatternPrev &&
            isEqual(parserResultPattern.expression.status, parserResultPatternPrev.expression.status)
            ? parserResultPatternBasic
            : basicFilters.convertToBasic(SchemaRules.flatten(parserResultPattern.expression.tree)) || null
          : {}
        : {}
      setParserResultPatternBasic(null === tree || 1 < Object.keys(tree).length ? null : tree)
    } catch {
      setParserResultPatternBasic(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkPattern, basicFilters, parserResultPattern, parserResultPatternError])

  const parserResultBase = useMemo(
    () =>
      data.parser.analyzeString({
        id: id,
        filter: basePattern,
        clusters: data.clusters,
        saveTokens: true,
        numberAllowed: true
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, data.surveySchema, data.clusters, basePattern]
  )
  const parserResultBaseError = useMemo(
    () =>
      0 < basePattern.length &&
      0 < parserResultBase.expression.status.errors.length &&
      parserResultBase.expression.status.errors.some(v => !['ref', 'cluster'].includes(v.type)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parserResultBase]
  )

  const itemMode = useMemo(() => {
    if ('advanced' == item.mode || changedRef.current) return item.mode

    return disabled ||
      !data.options.some(group => 0 < group.items.length) ||
      ((null === selectedOptionRef.current || 'inputtext' != selectedOptionRef.current.type) &&
        null === parserResultPatternBasic)
      ? 'advanced'
      : 'basic'
  }, [item.mode, data.options, disabled, parserResultPatternBasic])

  const itemModeRef = useRef(null)
  itemModeRef.current = itemMode

  const textBase = useMemo(
    () =>
      (30 < item.base.length && 'Base: ' + item.base.slice(0, 27) + '...') ||
      (0 < item.base.length && 'Base: ' + item.base) ||
      'Base',
    [item.base]
  )

  const fetchData = useCallback(() => {
    return {
      id,
      item,
      position: position - 1,
      windowUID: PageGlobals.WINDOW_UID,
      remove: () =>
        dispatch({
          type: 'DELETE_ANSWER_VARIANT',
          data: position - 1
        })
    }
  }, [item, position, id, dispatch])

  const onInputChange = event => setInput(event.target.value || '')

  const onInputPaste = event => {
    event.preventDefault()

    let text =
      (event.clipboardData || window.clipboardData).getData('Text') || event.clipboardData.getData('text/plain')
    text = text
      .replace(/(\r\n|\n|\r|\t|\v)/gm, ' ')
      .split(/\s*,\s*/)
      .flatMap(v => v.split(/\s+/))
      .filter(v => 0 < v.trim().length)
      .join(', ')

    const el = event.currentTarget

    const start = el.selectionStart
    const end = el.selectionEnd

    const valueBefore = el.value.slice(0, start)
    const valueAfter = el.value.slice(end)
    const valueBeforeFormatted = valueBefore
      .trim()
      .split(/\s*,\s*/)
      .flatMap(v => v.split(/\s+/))
      .filter(v => 0 < v.trim().length)
      .join(', ')
    const valueAfterFormatted = valueAfter
      .trim()
      .split(/\s*,\s*/)
      .flatMap(v => v.split(/\s+/))
      .filter(v => 0 < v.trim().length)
      .join(', ')

    const l = text.length
    const p = start - (valueBefore.length - valueBeforeFormatted.length) + l + (0 < valueBeforeFormatted.length ? 2 : 0)

    text = `${valueBeforeFormatted}${0 < valueBeforeFormatted.length ? ', ' : ''}${text}${
      0 < valueAfterFormatted.length ? ', ' : ''
    }${valueAfterFormatted}`

    setInput(text)

    callbackRef.current = () => {
      el.setSelectionRange(p, p)
      el.blur()
      el.focus()
    }
  }

  const onInputKeyPress = event => {
    const charCode = event.which == undefined ? event.keyCode : event.which
    const charString = String.fromCharCode(charCode)

    if ('inputtext' == selectedOption.type) {
      if (!/[\d\s,]/.test(charString)) event.preventDefault()

      const el = event.currentTarget

      const start = el.selectionStart
      const end = el.selectionEnd

      const valueBefore = el.value.slice(0, start)
      const valueAfter = el.value.slice(end)

      if ((/,/.test(charString) || /\s/.test(charString)) && ',' !== valueBefore.trim().slice(-1)) {
        event.preventDefault()

        const text = `${valueBefore}, ${valueAfter}`

        setInput(text)

        callbackRef.current = () => {
          el.setSelectionRange(start + 2, start + 2)
          el.blur()
          el.focus()
        }
      }
    }
  }

  const dndListItem = ADND.targets.itemsList.listItem({
    type: item.other ? `dlq-answer-other-${id}-${PageGlobals.WINDOW_UID}` : `dlq-answer-${PageGlobals.WINDOW_UID}`,
    list: dndItemsList
  })
  const dndListItemHandle = ADND.sources
    .listItemHandle({
      type: item.other ? `dlq-answer-other-${id}-${PageGlobals.WINDOW_UID}` : `dlq-answer-${PageGlobals.WINDOW_UID}`,
      item: dndListItem,
      cloneable: true
    })
    .fetchData(fetchData)

  const labelTemplate = value =>
    'sliderselect' == selectedOption.type ? (
      <span
        dangerouslySetInnerHTML={{
          __html: selectedOption.options[Math.min(Math.max(value, 1), selectedOption.options.length) - 1].text
        }}
      ></span>
    ) : (
      <span dangerouslySetInnerHTML={{ __html: selectedOption.options[value - 1].text }}></span>
    )

  const buttonLabelTemplate = event => {
    const text =
      'sliderselect' == selectedOption.type
        ? `${selectedOption.options[Math.max(event.sliderProps.value[0], 1) - 1].text} &ndash; ${
            selectedOption.options[Math.min(event.sliderProps.value[1], selectedOption.options.length) - 1].text
          }`
        : 1 < selectedOption.options.length &&
          selectedOption.options.length == event.value.length &&
          ('multistatemultiselect' !== selectedOption.type || !event.value.some(item => !item.state))
        ? 'All'
        : 0 < event.value.length
        ? 1 == event.value.length && ('multistatemultiselect' !== selectedOption.type || event.value[0].state)
          ? selectedOption.options.find(
              o => o.key == ('multistatemultiselect' == selectedOption.type ? event.value[0].value : event.value[0])
            ).text
          : `Filtered to ${event.value.length}`
        : 'Select...'
    return <span dangerouslySetInnerHTML={{ __html: text }}></span>
  }

  const _onModeChange = (mode, applyToAll = false) => {
    if (applyToAll)
      dispatch({
        type: 'UPDATE_ANSWER_VARIANTS_MODE',
        data: {
          positions: Object.keys(parserResultPatternBasicsRef.current).filter(
            k => null !== parserResultPatternBasicsRef.current[k]
          ),
          mode: mode
        }
      })
    else
      dispatch({
        type: 'UPDATE_ANSWER_VARIANT',
        data: {
          item: {
            ...item,
            mode: mode || ('basic' == item.mode ? 'advanced' : 'basic')
          },
          position: position - 1
        }
      })
  }
  const onModeChange = (mode, applyToAll = false) => {
    if ('advanced' == item.mode && false == item.check) {
      setCheckPattern(true)
    } else _onModeChange(mode, applyToAll)
  }

  const onResetValues = () => {
    setInput('')

    setSelectedValue(null)
    setSelectedValues([])
    setSelectedOption(null)

    if ('advanced' != item.mode) {
      onModeChange('advanced')
    }
  }

  const onSelectedChange = (value = null, _values) => {
    const group = data.options.find(v => v.items.some(v => v.key == value)) || null
    if (null !== group && group.disabled) return

    const option = group.items.find(v => v.key == value) || null
    if (null !== option && option.disabled) return

    const values = _values || (null !== option && 'sliderselect' == option.type ? [1, option.options.length] : [])

    if (null !== option && 'inputtext' != option.type) {
      values.sort((a, b) =>
        'multistatemultiselect' == option.type
          ? option.options.findIndex(v => v.key == a.value) - option.options.findIndex(v => v.key == b.value)
          : 'sliderselect' == option.type
          ? a - b
          : option.options.findIndex(v => v.key == a) - option.options.findIndex(v => v.key == b)
      )
    }

    setSelectedValue(value)
    setSelectedValues(values)
    setSelectedOption(option)

    changedRef.current = true

    dispatch({
      type: 'UPDATE_ANSWER_VARIANT',
      data: {
        item: {
          ...item,
          pattern:
            null === option
              ? ''
              : basicFilters.convertToAdvanced(
                  convertValuesToTree(
                    option,
                    /^TAG.P?Q\d+(?:SQ\d+)?./.test(value) ? value.match(/^(TAG.P?Q\d+(?:SQ\d+)?)./)[1] : value,
                    values
                  )
                )
        },
        position: position - 1
      }
    })
  }

  const onPatternChange = value => {
    setProcessing(true)

    onResetValues()

    dispatch({
      type: 'UPDATE_ANSWER_VARIANT',
      data: {
        item: { ...item, pattern: value },
        position: position - 1
      }
    })
  }

  const onLabelChange = event => {
    dispatch({
      type: 'UPDATE_ANSWER_VARIANT',
      data: {
        item: {
          ...item,
          text: encodeText(event.target.value)
        },
        position: position - 1
      }
    })
  }

  const onBaseChange = value => {
    setBaseMode(false)
    setBasePattern(value)

    item.base != value &&
      dispatch({
        type: 'UPDATE_ANSWER_VARIANT',
        data: {
          item: {
            ...item,
            base: value
          },
          position: position - 1
        }
      })
  }

  const onImageChange = value => {
    if ('off' == value) delete item.image
    else item.image = value

    dispatch({
      type: 'UPDATE_ANSWER_VARIANT',
      data: {
        item: {
          ...item
        },
        position: position - 1
      }
    })
  }

  const onDeleteClick = () => {
    dispatch({
      type: 'DELETE_ANSWER_VARIANT',
      data: position - 1
    })
  }

  const onHandleClick = useSingleAndDoubleClick(null, () => {
    dispatch({
      type: 'ADD_ANSWER_VARIANT',
      data: {
        item: {
          ...item,
          key: generateUID()
        },
        position: position
      }
    })
  })

  const onChange = event =>
    'sliderselect' == selectedOption.type
      ? onSelectedChange(selectedValue, [
          Math.max(event.value[0], 1),
          Math.min(event.value[1], selectedOption.options.length)
        ])
      : 'dropdown' == selectedOption.type
      ? onSelectedChange(selectedValue, [event.value])
      : onSelectedChange(selectedValue, event.value)

  useEffect(() => {
    null !== selectedOption && (dropdownsRef.current[position] = selectedOption.key)

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      delete dropdownsRef.current[position]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, selectedOption]) // TODO

  useEffect(() => {
    const keys = Object.keys(parserResultPatternBasic || {})

    if (0 < keys.length) {
      const options = data.options.flatMap(v => v.items)
      const option = /^TAG.P?Q\d+(?:SQ\d+)?$/.test(keys[0])
        ? options.find(
            v =>
              String(v.value || v.key)
                .toLowerCase()
                .startsWith(`${keys[0].toLowerCase()}.`) &&
              v.options.some(o =>
                Array.isArray(parserResultPatternBasic[keys[0]].v)
                  ? parserResultPatternBasic[keys[0]].v.some(v => o.key.toLowerCase() == v.toLowerCase())
                  : o.key.toLowerCase() == parserResultPatternBasic[keys[0]].v.toLowerCase()
              )
          ) || null
        : options.find(v => String(v.value || v.key).toLowerCase() == keys[0].toLowerCase()) || null

      parserResultPatternBasicsRef.current[position] = null !== option ? parserResultPatternBasic : null
    } else parserResultPatternBasicsRef.current[position] = 0 < item.pattern.trim().length ? null : {}

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      delete parserResultPatternBasicsRef.current[position]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.pattern, position, parserResultPatternBasic, data.options]) // TODO

  useEffect(() => {
    if (changedRef.current) {
      setProcessing(false)

      changedRef.current = false
      return
    }

    if (
      null !== selectedOptionRef.current &&
      'inputtext' == selectedOptionRef.current.type &&
      'basic' == itemModeRef.current
    ) {
      return
    }

    if (null === parserResultPatternBasic) {
      onResetValues()
    } else {
      const keys = Object.keys(parserResultPatternBasic || {})

      if (0 < keys.length) {
        const options = data.options.flatMap(v => v.items)
        const option = /^TAG.P?Q\d+(?:SQ\d+)?$/.test(keys[0])
          ? options.find(
              v =>
                String(v.value || v.key)
                  .toLowerCase()
                  .startsWith(`${keys[0].toLowerCase()}.`) &&
                v.options.some(o =>
                  Array.isArray(parserResultPatternBasic[keys[0]].v)
                    ? parserResultPatternBasic[keys[0]].v.some(v => o.key.toLowerCase() == v.toLowerCase())
                    : o.key.toLowerCase() == parserResultPatternBasic[keys[0]].v.toLowerCase()
                )
            ) || null
          : options.find(v => String(v.value || v.key).toLowerCase() == keys[0].toLowerCase()) || null

        if (null === option) {
          onResetValues()
        } else {
          const tree = Object.values(parserResultPatternBasic)[0]

          const values = convertTreeToValues(option, tree)

          if ('inputtext' == option.type) {
            setInput(values.join(', '))
          } else {
            setInput('')

            values.sort((a, b) =>
              'multistatemultiselect' == option.type
                ? option.options.findIndex(v => v.key == a.value) - option.options.findIndex(v => v.key == b.value)
                : 'sliderselect' == option.type
                ? a - b
                : option.options.findIndex(v => v.key == a) - option.options.findIndex(v => v.key == b)
            )
          }

          setSelectedValue(option.key)
          setSelectedValues(values)
          setSelectedOption(option)
        }
      }
    }

    setProcessing(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parserResultPatternBasic, data.options])

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    if (dndListItemHandle) dndListItemHandle.connect(dndListItemHandleRef.current)

    return () => {
      if (dndListItemHandle) dndListItemHandle.disconnect()
    }
  }, [dndListItemHandle])

  useEffect(() => {
    if (!changedRef.current || 'basic' == itemModeRef.current) {
      if (null === selectedOptionRef.current || 'inputtext' != selectedOptionRef.current.type) {
        callbackRef.current = null
      } else {
        if (null !== callbackRef.current && 'function' == typeof callbackRef.current) {
          callbackRef.current()
          callbackRef.current = null
        }

        timer.current && clearTimeout(timer.current)
        timer.current = setTimeout(() => {
          onSelectedChange(
            selectedValue,
            input.split(',').map(v => v.trim())
          )
        }, 300)
      }
    }

    return () => timer.current && clearTimeout(timer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input])

  useEffect(() => {
    dispatch({
      type: 'UPDATE_LOGIC_DATA',
      data: {
        id: `a${position}.pattern`,
        logicData: getLogicData(data.tags, data.codeBooks, parserResultPattern, parserResultPatternError)
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_LOGIC_DATA',
        data: {
          id: `a${position}.pattern`,
          logicData: null
        }
      })
    }
  }, [position, data.tags, data.codeBooks, dispatch, parserResultPattern, parserResultPatternError])
  useEffect(() => {
    dispatch({
      type: 'UPDATE_LOGIC_DATA',
      data: {
        id: `a${position}.base`,
        logicData: getLogicData(data.tags, data.codeBooks, parserResultBase, parserResultBaseError)
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_LOGIC_DATA',
        data: {
          id: `a${position}.base`,
          logicData: null
        }
      })
    }
  }, [position, data.tags, data.codeBooks, dispatch, parserResultBase, parserResultBaseError])
  useEffect(() => {
    const patternRefs = getRefs(parserResultPattern)
    const baseRefs = getRefs(parserResultBase)
    const clustersRefs = getClustersRefs(data.modelClusters, parserResultPattern).concat(
      getClustersRefs(data.modelClusters, parserResultBase)
    )

    dispatch({
      type: 'UPDATE_REFS',
      data: {
        id: `a${position}`,
        refs: [patternRefs, baseRefs, clustersRefs]
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_REFS',
        data: {
          id: `a${position}`,
          refs: null
        }
      })
    }
  }, [position, data.modelClusters, dispatch, parserResultPattern, parserResultBase])
  useEffect(() => {
    dispatch({
      type: 'UPDATE_ERRORS',
      data: {
        id: `a${position}`,
        error: item.other ? false : !item.pattern.trim().length || parserResultPatternError || parserResultBaseError
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_ERRORS',
        data: {
          id: `a${position}`,
          error: null
        }
      })
    }
  }, [item, position, dispatch, parserResultPatternError, parserResultBaseError])

  useEffect(
    () =>
      null !== selectedValue &&
      setSelectedOption(data.options.flatMap(v => v.items).find(v => v.key == selectedValue) || null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.options]
  )

  useEffect(
    () => item.mode !== itemModeRef.current && onModeChange(itemModeRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.mode]
  )

  useEffect(() => setCheckPattern('advanced' != itemMode || false != item.check), [item.check, itemMode])

  const renderOptions = () => (
    <AnswerVariantOptions
      position={position}
      value={selectedValue}
      options={data.options}
      dropdownsRef={dropdownsRef}
      onSelectedChange={onSelectedChange}
    />
  )
  const renderValues = () => {
    if (null === selectedOption || 'none' == selectedOption.type) {
      return <MultiSelect className="p-multiselect-rounded p-composite" placeholder="Select..." disabled />
    } else {
      switch (selectedOption.type) {
        case 'multistatemultiselect':
          return (
            <MultiStateMultiSelect
              className="p-multiselect-rounded p-composite"
              placeholder="Select..."
              value={selectedValues}
              options={selectedOption.options}
              optionValue="key"
              optionLabel="text"
              itemTemplate={itemTemplate}
              buttonLabelTemplate={buttonLabelTemplate}
              panelHeaderTemplate={MultiSelectHeader}
              panelFooterTemplate={MultiSelectFooter}
              filter
              filterBy="search,text"
              filterPlaceholder="Search items"
              resetFilterOnHide
              fixedDropdownDir="bottom"
              disabled={disabled}
              onChange={disabled ? null : onChange}
            />
          )
        case 'sliderselect': {
          return (
            <DropdownSlider
              dropdownClassName="p-dropdown-rounded p-composite"
              range
              min={1}
              max={selectedOption.options.length}
              value={selectedValues}
              labelTemplate={labelTemplate}
              buttonLabelTemplate={buttonLabelTemplate}
              // fixedDropdownDir="bottom"
              onChange={disabled ? null : onChange}
            />
          )
        }
        case 'multiselect': {
          return (
            <MultiSelect
              className="p-multiselect-rounded p-composite"
              placeholder="Select..."
              value={selectedValues}
              options={selectedOption.options}
              optionValue="key"
              optionLabel="text"
              itemTemplate={itemTemplate}
              buttonLabelTemplate={buttonLabelTemplate}
              panelHeaderTemplate={MultiSelectHeader}
              panelFooterTemplate={MultiSelectFooter}
              filter
              filterBy="search,text"
              filterPlaceholder="Search items"
              resetFilterOnHide
              fixedDropdownDir="bottom"
              disabled={disabled}
              onChange={disabled ? null : onChange}
            />
          )
        }
        case 'dropdown': {
          return (
            <Dropdown
              className="p-dropdown-rounded p-composite"
              placeholder="Select..."
              value={selectedValues[0]}
              options={selectedOption.options}
              optionValue="key"
              optionLabel="text"
              itemTemplate={itemTemplate}
              valueTemplate={valueTemplate}
              filter
              filterBy="search,text"
              filterPlaceholder="Search items"
              resetFilterOnHide
              filterInputAutoFocus={false}
              fixedDropdownDir="bottom"
              disabled={disabled}
              onChange={disabled ? null : onChange}
            />
          )
        }
        case 'inputtext': {
          return (
            <InputText
              className="p-inputtext-rounded p-composite"
              placeholder=""
              value={input}
              disabled={disabled}
              onChange={disabled ? null : onInputChange}
              onPaste={disabled ? null : onInputPaste}
              onKeyPress={disabled ? null : onInputKeyPress}
            />
          )
        }
        default: {
          return (
            <MultiSelect
              className="p-multiselect-rounded p-composite"
              placeholder="Select..."
              fixedDropdownDir="bottom"
              disabled
            />
          )
        }
      }
    }
  }

  const renderBaseMode = () => (
    <>
      <div className={`${style.base} p-fluid p-col-12`}>
        <AdvancedFilterContainer
          dataTestID={`stat-vq-answer-variant-base-${position}`}
          className={classNames('p-component p-inputtext p-inputtext-rounded p-inputtext-with-image', {
            'p-composite': !disabled,
            'p-composite-left': disabled
          })}
          pattern={basePattern}
          parser={data.parser}
          surveySchema={data.surveySchema}
          clusters={data.clusters}
          parserResultEof={parserResultEof}
          prefix="base = "
          hintPosition="top"
          options={{ numberAllowed: true }}
          readOnly={disabled}
          onChange={disabled ? null : setBasePattern}
        />
      </div>
      <span
        className={style.clear}
        aria-label="Clear base"
        title="Clear"
        {...pressable(disabled ? null : () => onBaseChange(''))}
      >
        <i className="icon-aytmfd-x-v2" />
      </span>
      <span
        className={style.apply}
        aria-label="Apply base"
        title="Apply"
        {...pressable(disabled || parserResultBaseError ? null : () => onBaseChange(basePattern.trim()))}
      >
        <i className="icon-aytmfd-checkmark" />
      </span>
    </>
  )
  const renderLabelMode = () => (
    <>
      {item.other || (disabled && !item.base.length) ? null : (
        <span
          data-testid={`stat-vq-answer-variant-base-toggle-${position}`}
          className={`${style['group-toggle']}${disabled ? ' ' + style['group-toggle-disabled'] : ''}`}
          {...pressable(disabled ? null : () => setBaseMode(true))}
        >
          {30 < item.base.length ? (
            <TextPopup text={textBase} content={item.base} direction="bottomright" portal />
          ) : (
            textBase
          )}
        </span>
      )}
      <div className={`${style.images} p-fluid p-col`}>
        <InputText
          className={classNames('p-inputtext-rounded p-inputtext-with-image', {
            'p-composite': !disabled,
            'p-composite-left': disabled
          })}
          placeholder="Please type a label..."
          value={decodeText(item.text)}
          maxLength={120}
          disabled={disabled}
          onChange={disabled ? null : onLabelChange}
        />
        <ImagesDropdown image={item.image} options={data.images} disabled={disabled} onImageChange={onImageChange} />
      </div>
    </>
  )
  const renderModes = () => (baseMode ? renderBaseMode() : renderLabelMode())

  return (
    <div
      data-testid={`stat-vq-answer-variant-${position}`}
      ref={dndListItemRef}
      className={`PRAYTMTheme ${style.row} ${style['row-av']}`}
    >
      {disabled ? null : (
        <span
          data-testid={`stat-vq-answer-variant-delete-${position}`}
          className={style.delete}
          aria-label="Delete answer variant"
          {...pressable(onDeleteClick)}
        >
          <i className="icon-aytmfd-x-v2" />
        </span>
      )}
      <div className={style.index}>A{position}.</div>
      <div className={`${style.group} ${style['group-av']}${disabled ? ' ' + style['group-av-disabled'] : ''} p-col`}>
        <div className={`${style.group} p-col-8`}>
          {item.other ? (
            <div className="p-col-12">
              <span className="p-component p-label">Everyone else</span>
            </div>
          ) : (
            <>
              <ModeToggle
                mode={itemMode}
                processing={processing}
                toggleDisabled={
                  null === parserResultPatternBasic ||
                  (0 < Object.keys(parserResultPatternBasic).length && null === selectedOption)
                }
                onModeChange={onModeChange}
              />
              {'advanced' == itemMode ? (
                <div className="p-fluid p-col-12">
                  <AdvancedFilterContainer
                    dataTestID={`stat-vq-answer-variant-pattern-${position}`}
                    className="p-component p-inputtext p-inputtext-rounded p-composite-right"
                    pattern={item.pattern}
                    parser={data.parser}
                    surveySchema={data.surveySchema}
                    clusters={data.clusters}
                    parserResultEof={parserResultEof}
                    hintPosition="top"
                    disabled={disabled}
                    onChange={disabled ? null : onPatternChange}
                  />
                </div>
              ) : (
                <>
                  <div className="p-fluid p-col-6">{renderOptions()}</div>
                  <div className="p-fluid p-col-6">{renderValues()}</div>
                </>
              )}
            </>
          )}
        </div>
        <div className={`${style.group} p-fluid p-col`}>{renderModes()}</div>
      </div>
      {disabled ? null : (
        <span
          data-testid={`stat-vq-answer-variant-handle-${position}`}
          ref={dndListItemHandleRef}
          className={style.handle}
          onClick={item.other ? null : onHandleClick}
        />
      )}
    </div>
  )
}

AnswerVariant.propTypes = {
  item: PropTypes.object,
  position: PropTypes.number,
  dndItemsList: PropTypes.any
}

AnswerVariant.displayName = 'AnswerVariant'

export default AnswerVariant
